/**
 * In-session negative cache for permanent client failures (Package F §3.4).
 * Prevents SWR focus/reconnect/interval from re-hitting APIs that will never
 * succeed for the current authz fingerprint.
 *
 * Permanent only for COMMERCIAL (code + structured kind) or
 * TENANT_ENTITLEMENT_HARD_OFF — never bare GET/HEAD 403, RBAC, READINESS/TECHNICAL,
 * or unknown. Mutations never permanent.
 *
 * 401 is intentionally excluded — TTL `requestFailureCache` covers auth storms.
 * Peer tabs sync denies via `ADMIN_CACHE_CHANNEL` (no tokens on the bus).
 */
import { ApiError } from '../../services/api/apiClient.types.js';
import type { ApiErrorEnvelope } from '../../services/api/apiClient.types.js';
import { adminCacheBus } from '../crossTab/adminCrossTab.js';
import { getTenantFromPath } from '../tenant.js';

const ENTITLEMENT_READ_DENIED = 'TENANT_ENTITLEMENT_READ_DENIED';
const ENTITLEMENT_WRITE_DENIED = 'TENANT_ENTITLEMENT_WRITE_DENIED';
const ENTITLEMENT_HARD_OFF = 'TENANT_ENTITLEMENT_HARD_OFF';
const INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS';

/** Permanent deny kinds only (Package F §3.4) — never READINESS/TECHNICAL/RBAC. */
const PERMANENT_DENY_KINDS = new Set(['COMMERCIAL', 'HARD_OFF']);

export interface EntitlementDenialWireItem {
  readonly blockKey?: string;
  readonly kind?: string;
}

/**
 * Session fingerprint dimensions from `/admin/me` (Spec §3.4).
 * Permanent deny entries are valid only while these match.
 *
 * `accessRevision` is an opaque cache-bust token (optional until BE emits it).
 * SHADOW: never treat shadow-only metadata as commercial deny authority — this
 * field is invalidation-only; do not invent upgrade UX from it.
 */
export interface ApiDenySessionFingerprint {
  readonly entitlementRevision: number | null;
  /** Opaque `/admin/me` access revision; null when absent. Invalidation only. */
  readonly accessRevision: string | null;
  readonly identityId: number | string | null;
  readonly capabilitiesKey: string;
  readonly tenantScopeKey: string;
  readonly tenantScopeCodesKey: string;
}

export function buildApiDenySessionFingerprint(input: {
  readonly entitlementRevision?: number | null;
  readonly accessRevision?: string | null;
  readonly identityId?: number | string | null;
  readonly capabilities?: readonly string[] | null;
  readonly tenantScope?: number[] | '*' | null;
  readonly tenantScopeCodes?: readonly string[] | null;
}): ApiDenySessionFingerprint {
  const capabilities = input.capabilities ?? [];
  const tenantScopeCodes = input.tenantScopeCodes ?? [];
  let tenantScopeKey = '';
  if (input.tenantScope === '*') {
    tenantScopeKey = '*';
  } else if (Array.isArray(input.tenantScope)) {
    tenantScopeKey = input.tenantScope.join(',');
  }
  return {
    entitlementRevision:
      typeof input.entitlementRevision === 'number' && Number.isFinite(input.entitlementRevision)
        ? input.entitlementRevision
        : null,
    accessRevision: normalizeAccessRevision(input.accessRevision),
    identityId: input.identityId ?? null,
    capabilitiesKey: [...capabilities].join('\0'),
    tenantScopeKey,
    tenantScopeCodesKey: [...tenantScopeCodes].join('\0'),
  };
}

/** Coerce optional `/admin/me` accessRevision wire value to fingerprint dimension. */
export function normalizeAccessRevision(
  value: string | null | undefined,
): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function sameApiDenySessionFingerprint(
  a: ApiDenySessionFingerprint | null | undefined,
  b: ApiDenySessionFingerprint | null | undefined,
): boolean {
  if (a == null && b == null) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  return (
    a.entitlementRevision === b.entitlementRevision &&
    a.accessRevision === b.accessRevision &&
    a.identityId === b.identityId &&
    a.capabilitiesKey === b.capabilitiesKey &&
    a.tenantScopeKey === b.tenantScopeKey &&
    a.tenantScopeCodesKey === b.tenantScopeCodesKey
  );
}

/**
 * Extract structured entitlement deny kinds from ApiError envelope / details /
 * optional typed details bag (parsers may land in parallel).
 */
export function extractEntitlementDenialKinds(
  errorOrDetails: unknown,
): readonly string[] {
  if (errorOrDetails instanceof ApiError) {
    const fromTyped = errorOrDetails.details?.entitlementDenials;
    if (Array.isArray(fromTyped)) {
      return kindsFromDenialItems(fromTyped);
    }
    return extractEntitlementDenialKinds(errorOrDetails.response);
  }
  if (typeof errorOrDetails !== 'object' || errorOrDetails === null) {
    return [];
  }
  const asEnvelope = errorOrDetails as {
    details?: unknown;
    entitlementDenials?: unknown;
  };
  const detailsBag =
    typeof asEnvelope.details === 'object' && asEnvelope.details !== null
      ? (asEnvelope.details as { entitlementDenials?: unknown }).entitlementDenials
      : undefined;
  if (Array.isArray(detailsBag)) {
    return kindsFromDenialItems(detailsBag);
  }
  if (Array.isArray(asEnvelope.entitlementDenials)) {
    return kindsFromDenialItems(asEnvelope.entitlementDenials);
  }
  return [];
}

function kindsFromDenialItems(denials: readonly unknown[]): readonly string[] {
  const kinds: string[] = [];
  for (const item of denials) {
    if (
      typeof item === 'object' &&
      item !== null &&
      typeof (item as { kind?: unknown }).kind === 'string'
    ) {
      kinds.push((item as { kind: string }).kind);
    }
  }
  return kinds;
}

function hasOnlyPermanentDenyKinds(kinds: readonly string[]): boolean {
  if (kinds.length === 0) {
    return false;
  }
  return kinds.every((k) => PERMANENT_DENY_KINDS.has(k));
}

/** Stable route families (path after `/api/{tenant}/` or `/api/` for unscoped). */
export const API_DENY_ROUTES = {
  analyticsSummary: 'v1/admin/analytics/summary',
  analyticsBasicSummary: 'v1/admin/analytics/basic-summary',
  analyticsCohorts: 'v1/admin/analytics/cohorts',
  analyticsBaskets: 'v1/admin/analytics/baskets',
  analyticsBenchmark: 'v1/admin/analytics/benchmark',
  exploreBounds: 'v1/admin/analytics/explore/bounds',
  exploreSummary: 'v1/admin/analytics/explore/summary',
  exploreTimeseries: 'v1/admin/analytics/explore/timeseries',
  exploreCooccurrence: 'v1/admin/analytics/explore/cooccurrence',
  exploreDrilldown: 'v1/admin/analytics/explore/drilldown',
  analyticsFreshness: 'v1/admin/analytics/freshness',
  analyticsFeatures: 'v1/admin/analytics/features',
  analyticsIngestQuality: 'v1/admin/analytics/ingest-quality',
  devExploreBounds: 'v1/dev/analytics/explore/bounds',
  devExploreSummary: 'v1/dev/analytics/explore/summary',
  devExploreTimeseries: 'v1/dev/analytics/explore/timeseries',
  devExploreCooccurrence: 'v1/dev/analytics/explore/cooccurrence',
  devExploreDrilldown: 'v1/dev/analytics/explore/drilldown',
} as const;

export type ApiDenyRouteFamily =
  (typeof API_DENY_ROUTES)[keyof typeof API_DENY_ROUTES];

interface DenyCacheWriteOptions {
  /** Apply a remote peer signal — do not rebroadcast (loop guard). */
  readonly fromCrossTab?: boolean;
}

/** key → fingerprint bound at mark time (null = unbound / pre-fingerprint). */
const deniedEntries = new Map<string, ApiDenySessionFingerprint | null>();
let sessionFingerprint: ApiDenySessionFingerprint | null = null;
let crossTabInitialized = false;

export function getApiDenySessionFingerprint(): ApiDenySessionFingerprint | null {
  return sessionFingerprint;
}

/**
 * Bind permanent denies to the current `/admin/me` fingerprint.
 * Call after successful /me (AuthProvider). Does not flush the cache.
 */
export function setApiDenySessionFingerprint(
  fingerprint: ApiDenySessionFingerprint | null,
): void {
  sessionFingerprint = fingerprint;
}

export function buildApiDenyKey(input: {
  readonly tenantCode: string | null;
  readonly routeFamily: string;
  readonly method?: string;
}): string {
  const method = (input.method ?? 'GET').toUpperCase();
  const tenant = input.tenantCode && input.tenantCode.length > 0 ? input.tenantCode : '_';
  return `${tenant}|${method}|${input.routeFamily}`;
}

/**
 * Normalize an API path (with or without tenant segment / query) to a route family.
 * `/api/acme/v1/admin/analytics/cohorts?x=1` → `v1/admin/analytics/cohorts`
 * `/api/v1/dev/analytics/explore/bounds` → `v1/dev/analytics/explore/bounds`
 */
export function routeFamilyFromApiPath(endpoint: string): string {
  const pathOnly = (endpoint.split('?')[0] ?? endpoint).replace(/\/+$/, '') || '/';
  const tenantScoped = pathOnly.match(/^\/api\/([^/]+)\/(.+)$/);
  if (tenantScoped?.[1] != null && tenantScoped[2] != null) {
    if (tenantScoped[1] === 'v1') {
      return `v1/${tenantScoped[2]}`;
    }
    return tenantScoped[2];
  }
  if (pathOnly.startsWith('/api/')) {
    return pathOnly.slice('/api/'.length);
  }
  return pathOnly.replace(/^\//, '');
}

/** Extract tenant code from a tenant-prefixed API path, else null. */
export function tenantCodeFromApiPath(endpoint: string): string | null {
  const pathOnly = endpoint.split('?')[0] ?? endpoint;
  const match = pathOnly.match(/^\/api\/([^/]+)\//);
  if (match?.[1] == null || match[1] === 'v1') {
    return null;
  }
  return match[1];
}

export function denyKeyForApiPath(
  endpoint: string,
  method = 'GET',
  tenantCodeOverride?: string | null,
): string {
  const fromPath = tenantCodeFromApiPath(endpoint);
  const tenantCode =
    tenantCodeOverride !== undefined
      ? tenantCodeOverride
      : (fromPath ?? getTenantFromPath());
  return buildApiDenyKey({
    tenantCode,
    routeFamily: routeFamilyFromApiPath(endpoint),
    method,
  });
}

export function denyKeyForRouteFamily(
  routeFamily: string,
  method = 'GET',
  tenantCode?: string | null,
): string {
  return buildApiDenyKey({
    tenantCode: tenantCode !== undefined ? tenantCode : getTenantFromPath(),
    routeFamily,
    method,
  });
}

export function markDenied(key: string, options?: DenyCacheWriteOptions): void {
  const alreadyDenied = deniedEntries.has(key);
  deniedEntries.set(key, sessionFingerprint);
  if (alreadyDenied || options?.fromCrossTab === true) {
    return;
  }
  adminCacheBus.publish({ type: 'api-deny', denyKey: key });
}

/**
 * True only when the key is denied **and** still bound to the current session
 * fingerprint. Stale entries after authz revision are dropped (no synthetic
 * `API_DENY_CACHED` across mid-session authz).
 */
export function isDenied(key: string): boolean {
  if (!deniedEntries.has(key)) {
    return false;
  }
  if (sessionFingerprint === null) {
    return false;
  }
  const bound = deniedEntries.get(key) ?? null;
  if (!sameApiDenySessionFingerprint(bound, sessionFingerprint)) {
    deniedEntries.delete(key);
    return false;
  }
  return true;
}

export function clearDenied(key?: string, options?: DenyCacheWriteOptions): void {
  if (key === undefined) {
    deniedEntries.clear();
  } else {
    deniedEntries.delete(key);
  }
  if (options?.fromCrossTab === true) {
    return;
  }
  if (key === undefined) {
    adminCacheBus.publish({ type: 'api-deny-clear' });
    return;
  }
  adminCacheBus.publish({ type: 'api-deny-clear', denyKey: key });
}

/**
 * Subscribe once to peer-tab deny / clear signals.
 * Safe to call repeatedly; additional calls are no-ops.
 */
export function initApiDenyCacheCrossTab(): void {
  if (crossTabInitialized) {
    return;
  }
  crossTabInitialized = true;
  adminCacheBus.subscribe((message) => {
    if (message.type === 'api-deny') {
      markDenied(message.denyKey, { fromCrossTab: true });
      return;
    }
    if (message.type === 'api-deny-clear') {
      clearDenied(message.denyKey, { fromCrossTab: true });
    }
  });
}

/**
 * Package F §3.4 — permanent only for COMMERCIAL / HARD_OFF (code + structured kind).
 * Generic/unknown 403, RBAC, READINESS/TECHNICAL — never permanent.
 * Mutations never permanent.
 */
export function isPermanentClientDeny(
  status: number,
  code?: string | undefined,
  method = 'GET',
  entitlementDenyKinds?: readonly string[] | undefined,
): boolean {
  const normalizedMethod = method.toUpperCase();
  if (normalizedMethod !== 'GET' && normalizedMethod !== 'HEAD') {
    return false;
  }
  if (status !== 403) {
    return false;
  }
  if (code === INSUFFICIENT_PERMISSIONS) {
    return false;
  }
  if (code === ENTITLEMENT_HARD_OFF) {
    return true;
  }
  const kinds = entitlementDenyKinds ?? [];
  if (kinds.length > 0) {
    // Mixed deny with non-permanent kinds (READINESS/TECHNICAL/…) → never permanent
    if (!hasOnlyPermanentDenyKinds(kinds)) {
      return false;
    }
    return true;
  }
  // No structured kinds: bare READ/WRITE_DENIED or generic 403 — never permanent
  if (code === ENTITLEMENT_READ_DENIED || code === ENTITLEMENT_WRITE_DENIED) {
    return false;
  }
  return false;
}

/**
 * AuthProvider cookie-first boot probes GET `/admin/me` while logged out
 * (expected 401; G3: at most one Network identity probe on cold login).
 * Kept for requestFailureCache /me exemption and callers; 401 no longer writes
 * permanent deny for any route (see {@link isPermanentClientDeny}).
 */
export function isAdminMeRouteFamily(routeFamily: string): boolean {
  return (
    routeFamily === 'v1/admin/me' ||
    routeFamily === 'admin/me' ||
    routeFamily.endsWith('/admin/me')
  );
}

/** Whether a failure should be written into the permanent deny cache. */
export function shouldMarkApiDenied(
  status: number,
  code: string | undefined,
  method: string,
  _routeFamily: string,
  entitlementDenyKinds?: readonly string[] | undefined,
): boolean {
  return isPermanentClientDeny(status, code, method, entitlementDenyKinds);
}

export function markDeniedFromError(key: string, error: unknown): void {
  if (!(error instanceof ApiError)) {
    return;
  }
  // Keys are `tenant|METHOD|routeFamily` — last segment is the route family.
  const parts = key.split('|');
  const routeFamily = parts.length > 0 ? (parts[parts.length - 1] ?? key) : key;
  const kinds = extractEntitlementDenialKinds(error);
  if (shouldMarkApiDenied(error.statusCode, error.code, 'GET', routeFamily, kinds)) {
    markDenied(key);
  }
}

export function isRouteDenied(routeFamily: string, method = 'GET'): boolean {
  return isDenied(denyKeyForRouteFamily(routeFamily, method));
}

/** @internal test helper — reset fingerprint without publishing. */
export function resetApiDenySessionFingerprintForTests(): void {
  sessionFingerprint = null;
}

/** Narrow helper for callers that already have an ApiErrorEnvelope.details bag. */
export function entitlementDenyKindsFromEnvelopeDetails(
  details: ApiErrorEnvelope['details'] | null | undefined,
): readonly string[] {
  if (details == null) {
    return [];
  }
  return extractEntitlementDenialKinds(details);
}
